import React, { useState } from "react";
import {
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import DropdownItem from './dropdownItem'

/**
 * Normaliza para comparar: sin tildes y en minúsculas.
 *
 * El filtro del dropdown comparaba en mayúsculas, así que "ñunoa" no
 * encontraba "Ñuñoa" y "san martin" no encontraba "San Martín". Con listas
 * cortas era tolerable; con los 135 partidos de Buenos Aires en el selector de
 * comuna deja de serlo. Se normalizan AMBOS lados, así el comportamiento
 * anterior (insensible a mayúsculas) se conserva como caso particular.
 *
 * Rango U+0300–U+036F = combining diacritical marks, lo que deja NFD al
 * descomponer los caracteres acentuados.
 */
const normalizeForSearch = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export default function SearchDropdown({
  options,
  placeholder,
  selected,
  onChange,
  disabled,
  className,
  tooltip,
  ariaLabel,
  ariaBusy,
  /**
   * Opcional. `(option, query) => boolean`: decide si la opción se muestra para el texto
   * escrito. Sirve para buscar por campos que no están en el `label` (p. ej. la razón
   * social de un comercio cuyo label es el nombre comercial). `query` llega tal cual lo
   * escribió el usuario: la función decide cómo compararlo. Sin ella se filtra por el
   * label, sin tildes ni mayúsculas, como siempre.
   */
  filterOption,
  /**
   * Opcional. `(option) => string`: segunda línea bajo el label (p. ej. "razón social ·
   * RUT"). Si no se pasa, o devuelve vacío, el ítem se ve como siempre.
   */
  getOptionSecondary,
}) {
  const [dropdownToggle, setDropdownToggle] = useState(false);
  const [search, setSearch] = useState("");

  const callback = (value, label) => {
    onChange(value);
    setDropdownToggle(false);
    setSearch(label || "");
  };

  const getDisplayText = (item) => {
    if (typeof item === 'string') { 
      return item 
    };
    return item?.label || item?.value || ''; // Fallback a cadena vacía si no hay 'name' ni 'label'
  };

  return (
    <Dropdown
      isOpen={dropdownToggle}
      toggle={() => options.length > 0 && setDropdownToggle(!dropdownToggle)}
      direction="down"
      disabled={disabled}
      className={className}
      aria-label={ariaLabel}
      aria-busy={ariaBusy === true ? true : undefined}
    >
      <DropdownToggle
        className={`d-flex align-items-center p-0 custom-searchable-dropdown w-100 ${
          disabled && "custom-searchable-dropdown__disabled"
        }`}
        disabled={disabled}
      >
        {dropdownToggle ? (
          <Input
            multiple={true}
            className="forms-custom-input-search-dropdown"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            placeholder={placeholder}
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.code === "Space") {
                e.preventDefault();
                setSearch(search + " ");
              }
            }}
          />
        ) : (
          <Input
            className="forms-custom-input-search-dropdown"
            onChange={(e) => setSearch(e.target.value)}
            value={getDisplayText(selected) || ''}
            placeholder={placeholder}
            disabled={disabled}
          />
        )}
      </DropdownToggle>
      <DropdownMenu className="w-100 custom-search-dropdown-menu">
        {options?.map((option, index) => {
          const displayText = getDisplayText(option);
          const matches = filterOption
            ? filterOption(option, search)
            : normalizeForSearch(displayText).includes(normalizeForSearch(search));
          if (matches) {
            return (
              <DropdownItem 
              key={index}
              value={option?.value} 
              label={option?.label} 
              secondary={getOptionSecondary?.(option)}
              callback={() => callback(option?.value, option?.label)} 
              setSearch={setSearch} 
              tooltip={tooltip} />
            );
          }
          return null;
        })}
      </DropdownMenu>
    </Dropdown>
  );
}
